













































import Vue from 'vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import Component from 'vue-class-component';
import ShoppingCartProductItem from './shopping-cart-product-item.vue';
import { ShoppingCartOrderDetail, ShoppingCartProduct } from '@accounting/interfaces/shopping-cart';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import CouponInput from '@accounting/components/others/coupon-input/coupon-input.vue';
import ShoppingCartEmpty from './shopping-cart-empty.vue';

@Component({
  name: 'bpc-shopping-cart-wizard-product-list-step',
  components: {
    'bpc-shopping-cart-product-item': ShoppingCartProductItem,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-coupon-input': CouponInput,
    'bpc-shopping-cart-empty': ShoppingCartEmpty,
  },
})
export default class ShoppingCartWizardProductListStep extends Vue {
  $refs: {
    cancelConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
  };
  listToCheckout: number[] = [];

  shoppingCartProducts: ShoppingCartProduct[] = [];
  loading: boolean = true;

  selectedProducts = {};
  orderToCancel: ShoppingCartOrderDetail = null;
  mounted() {
    this.orderToCancel = null;
    this.getProductsInShoppingCart();
  }

  async getProductsInShoppingCart(): Promise<void> {
    this.loading = true;
    const fetchEntityPath = this.$bpcApiRouteHelper.getApiPath('shopping-cart/products', 'raw');

    const queryConfig = {
      params: {},
    };

    this.$bpcApi.get(fetchEntityPath, queryConfig, 'public').subscribe(
      (res: BPCGenericApiResponse) => {
        this.shoppingCartProducts = res.data;
        this.initSelectedProducts();
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  get shoppingCartEmpty(): boolean {
    return !this.loading && !(this.shoppingCartProducts && this.shoppingCartProducts.length);
  }

  get showCouponInput(): boolean {
    return !this.couponAlreadyExists && this.couponEnabled;
  }

  get couponAlreadyExists(): boolean {
    return this.shoppingCartProducts.some(product => product.type === 'coupon');
  }

  get couponEnabled(): boolean {
    return !!this.$store.getters['centerSettings/allowCoupon'];
  }

  get nextStepDisabled(): boolean {
    return !Object.values(this.selectedProducts).some(val => val === true);
  }

  get cancelConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'cancel-order-modal',
      title: this.$t('global.modals.cancel-order.title'),
      body: this.$t('global.modals.cancel-order.body', {
        activity_group_name: this.orderToCancel ? this.orderToCancel.description : null,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }

  showCancelOrderModal(orderDetail: ShoppingCartOrderDetail) {
    this.orderToCancel = orderDetail;
    this.$refs.cancelConfirmationModal.onOpenModal(orderDetail.id);
  }

  async confirmCancelOrder(orderDetail: ShoppingCartOrderDetail) {
    this.loading = true;
    const data = {
      cancel_reason: 'other',
      cancel_reason_description: this.$t('global.cancelled_in_shopping_cart'),
    };

    if (orderDetail.type === 'enrollment') {
      // TODO it should be this way for all the types, but we only have enrollments implemented so far
      const path = this.$bpcApiRouteHelper.getApiPath(`shopping-cart/product/${orderDetail.id}`);
      await this.$bpcApi.delete(path, data).toPromise();
    } else {
      const path = `${this.$bpcApiRouteHelper.getEntityPath('order')}/cancel/${orderDetail.id}`;
      await this.$bpcApi.put(path, data).toPromise();
    }
    this.$refs.cancelConfirmationModal.onCloseModal();
    await this.getProductsInShoppingCart();

    // NOTE not sure if we should emit this here
    this.loading = false;
  }

  async onCouponAdded() {
    await this.getProductsInShoppingCart();
  }

  initSelectedProducts() {
    this.shoppingCartProducts.forEach(product => {
      this.$set(this.selectedProducts, `${product.type}_${product.id}`, true);
    });
  }

  get selectedOrders(): ShoppingCartOrderDetail[] {
    return this.shoppingCartProducts
      .filter(product => {
        return this.selectedProducts[`${product.type}_${product.id}`];
      })
      .reduce((res, product) => {
        res = res.concat(product.order_details);
        return res;
      }, []);
  }

  bulkSetSelectedProduct(value = true) {
    Object.keys(this.selectedProducts).forEach(key => {
      this.$set(this.selectedProducts, key, value);
    });
  }

  nextStepClicked() {
    if (this.nextStepDisabled) {
      this.$bpcMessage.showWarningMessage({
        text: 'accounting.shopping_cart_warning_no_product_selected',
      });
      return;
    }
    // get all the selected orders
    // emit productSelected event
    this.$emit('product-selected', this.selectedOrders);
  }
}
